import { getBrandDefaultPermitTypeUnit } from '@src/common/measurements/getBrandDefaultUnitSystem';
import type { ConditionSheets } from '@src/permits/completePermit/fields/ConditionSheets';
import {
  ConditionSheetsField,
  ShowInstructionsToClientField,
} from '@src/permits/fields/Fields';
import type { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import type { CustomDocumentFile } from '@src/permitTypesManager/EditPermitType';
import type { ExistingPermitTypeDefinition } from '@src/permitTypesManager/PermitTypeDefinition';
import { CustomDocumentPdfFields } from '@src/permitTypesManager/permitTypeSections/CustomDocumentPdfFields';
import {
  CurrencyField,
  HighPriorityField,
  JurisdictionField,
  JurisdictionUrlField,
  NameField,
  ShowToClientField,
  TypeField,
  UnitSystemFields,
  ValidateExpirationDateField,
  ValidateExpirationTimeField,
} from '@src/permitTypesManager/permitTypeSections/PermitDefinitionFields';
import { PermitInstructionsField } from '@src/permitTypesManager/permitTypeSections/PermitInstructionsField';

export enum PermitTypeFormMode {
  Edit = 'edit',
  Duplicate = 'duplicate',
}

export const permitTypeToFormDefaults = (
  permitType: ExistingPermitTypeDefinition,
  conditionSheets: ConditionSheets,
  mode: PermitTypeFormMode,
  customDocumentFile?: CustomDocumentFile,
): AddPermitTypeFormInputs => ({
  [NameField.fieldName]:
    mode === PermitTypeFormMode.Duplicate ? '' : permitType.name,
  [JurisdictionUrlField.fieldName]:
    !permitType.isSelfIssued && permitType.jurisdictionUrl
      ? permitType.jurisdictionUrl
      : undefined,
  [JurisdictionField.fieldName]: permitType.jurisdiction,
  [TypeField.fieldName]: permitType.type,
  [CurrencyField.fieldName]: permitType.currency,
  [HighPriorityField.fieldName]: permitType.highPriority,
  [ShowToClientField.fieldName]: permitType.showToClient,
  [ValidateExpirationDateField.fieldName]: permitType.validateExpirationDate,
  [ValidateExpirationTimeField.fieldName]: permitType.validateExpirationTime,
  [ShowInstructionsToClientField.fieldName]:
    permitType.showInstructionsToClient,
  [PermitInstructionsField.fieldName]: permitType.instructions
    ? permitType.instructions
    : undefined,
  [CustomDocumentPdfFields.fieldName]:
    !!customDocumentFile && 'file' in customDocumentFile
      ? customDocumentFile.file
      : undefined,
  [UnitSystemFields.fieldName]:
    permitType.unit ?? getBrandDefaultPermitTypeUnit(),
  ...(permitType.fields &&
    permitType.fields.reduce(
      (acc, fieldName) => {
        acc[fieldName] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    )),
  [ConditionSheetsField.fieldName]:
    mode === PermitTypeFormMode.Duplicate
      ? conditionSheets
          .filterForJurisdiction(permitType.jurisdiction)
          .toDefaultForm()
      : conditionSheets
          .filterForJurisdiction(permitType.jurisdiction)
          .toFormBasedOn(permitType),
});
